import { Box, Stack, SvgIconProps, Typography } from "@mui/material";
import React, { PropsWithChildren, ReactElement } from "react";
import { SBPalette } from "sonobello.utilities.react.mui";

import EnvironmentConfiguration from "../../types/EnvironmentConfiguration";
import { isMobileView } from "../../types/Constants";
import NextButtonDesktop from "../../steps/Components/NextButtonDesktop";
import NextButtonMobile from "../../steps/Components/NextButtonMobile";
import INextButtonProps from "../../steps/types/INextButtonProps";
import NeedHelpDialog from "./NeedHelpDialog";

interface ViewWrapperProps {
  /** The header text for the view. */
  header: string;
  /** Optional icon to render at the top of the view. It should use {@link ViewWrapperIconProps}.*/
  icon?: ReactElement;
  /** Optional custom props for the view Next button.
   * Defaults to the 'Back to Sono Bello' button behavior.
   */
  nextButtonProps?: Omit<INextButtonProps, "isLoading">;
  /** Optional override to hide the 'need help' dialog. Defaults to false. */
  hideNeedHelpDialog?: boolean;
  /** Optional subheader text for the view. */
  subheader?: string;
}

const defaultButtonProps: INextButtonProps = {
  label: "Back to Sono Bello",
  onClick: EnvironmentConfiguration.escapeUrl,
  isLoading: false
};

export const ViewWrapperIconProps: SvgIconProps = { sx: { fontSize: 70 }, color: "primary" };

const ViewWrapper: React.FC<PropsWithChildren<ViewWrapperProps>> = ({
  header,
  icon,
  nextButtonProps = defaultButtonProps,
  hideNeedHelpDialog,
  subheader,
  children
}) => {
  const showMobile = isMobileView();
  return (
    <Stack
      spacing={2}
      sx={{
        height: "100%",
        px: showMobile ? 2 : 6,
        backgroundColor: SBPalette.light.white.main,
        pb: showMobile ? 10 : 0
      }}
    >
      <Box
        width="100%"
        display="flex"
        justifyContent="center"
        mb={showMobile ? 0 : 3}
        paddingTop={showMobile ? 2 : "25%"}
      >
        {icon}
      </Box>
      <Stack spacing={1}>
        <Typography id="viewWrapper_header" variant={showMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
          {header}
        </Typography>
        {subheader && (
          <Typography id="viewWrapper_subheader" variant="body1">
            {subheader}
          </Typography>
        )}
      </Stack>
      <Box>{children}</Box>
      {!hideNeedHelpDialog && <NeedHelpDialog />}
      {showMobile ? (
        <NextButtonMobile {...nextButtonProps} isLoading={false} />
      ) : (
        <NextButtonDesktop {...nextButtonProps} isLoading={false} />
      )}
    </Stack>
  );
};

export default ViewWrapper;
